/**
 * Quote PDF generator.
 *
 * Listens for clicks on [data-quote-trigger] elements anywhere on the page,
 * collects the relevant items (product page: from embedded JSON; cart: via
 * /cart.js), opens the <quote-modal> dialog, and on submit POSTs to /contact
 * and downloads a Letter-size PDF built with jsPDF + autoTable.
 */

const JSPDF_SRC = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
const AUTOTABLE_SRC = 'https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.0/dist/jspdf.plugin.autotable.min.js';
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// How long submit waits for a Turnstile token before reserving without one.
// Long enough for a shopper to finish an interactive challenge.
const TURNSTILE_WAIT_MS = 30000;

const OUT_OF_STOCK_MESSAGE =
  "One or more items are out of stock and can't be quoted. Remove them from your cart to continue.";

let libsLoadingPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load ' + src));
    document.head.appendChild(s);
  });
}

function ensureLibsLoaded() {
  if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve();
  if (libsLoadingPromise) return libsLoadingPromise;
  libsLoadingPromise = loadScript(JSPDF_SRC)
    .then(() => loadScript(AUTOTABLE_SRC));
  return libsLoadingPromise;
}

let turnstileLoadingPromise = null;

function ensureTurnstileLoaded() {
  if (window.turnstile) return Promise.resolve();
  if (!turnstileLoadingPromise) {
    turnstileLoadingPromise = loadScript(TURNSTILE_SRC).catch((err) => {
      // Let a later open retry (e.g. after a flaky network).
      turnstileLoadingPromise = null;
      throw err;
    });
  }
  return turnstileLoadingPromise;
}

function fetchAsDataURL(url) {
  return fetch(url, { mode: 'cors' })
    .then((r) => {
      if (!r.ok) throw new Error('Image fetch failed: ' + r.status);
      return r.blob();
    })
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        })
    )
    .catch((err) => {
      console.warn('[quote] image fetch failed for', url, err);
      return null;
    });
}

function getImageNaturalSize(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ w: 0, h: 0 });
    img.src = dataUrl;
  });
}

function shopifyImageUrl(src, width) {
  if (!src) return '';
  // Insert _{width}x before the extension on Shopify CDN URLs.
  // Handles both protocol-relative and absolute URLs.
  return src.replace(/(\.(?:png|jpe?g|webp|gif))(\?.*)?$/i, `_${width}x$1$2`);
}

function generateLocalQuoteNumber() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  let suffix;
  if (window.crypto && window.crypto.randomUUID) {
    suffix = window.crypto.randomUUID().replace(/-/g, '').slice(0, 4).toUpperCase();
  } else {
    suffix = Math.random().toString(16).slice(2, 6).toUpperCase();
  }
  return `Q-${yyyy}${mm}${dd}-${suffix}`;
}

/**
 * Read the variant-id -> on-hand map that quote-button.liquid stamps onto the
 * trigger. Values are Shopify's `variant.inventory_quantity`; a null value (or
 * a missing variant) means the line is uncapped, which is what the snippet
 * emits for untracked variants and for ones the merchant lets oversell.
 */
function readInventoryFromTrigger(triggerEl) {
  const raw = triggerEl.dataset.quoteInventory;
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (err) {
    console.warn('[quote] could not parse inventory map', err);
    return {};
  }
}

/**
 * Reserve a canonical quote number from CVInsight. `payload.turnstileToken`
 * carries the bot-check token (required once CVInsight has a Turnstile secret).
 * Returns:
 *   - { id, quoteNumber, uploadToken }       on success (uploadToken authorizes
 *                                            the one PDF upload for this quote)
 *   - { conflict: true, error, details }     on 409 (stock moved under us)
 *   - null                                   if not configured, or any other
 *                                            failure — the modal then falls
 *                                            back to a local quote number and
 *                                            the customer still gets a PDF;
 *                                            only CVInsight tracking is
 *                                            skipped.
 * A 409 is the authoritative "not enough stock" answer and is never soft-failed
 * into a local quote number: the caller aborts and re-clamps instead.
 */
async function reserveQuoteFromCvinsight(baseUrl, apiKey, payload) {
  if (!baseUrl || !apiKey) return null;
  try {
    const url = baseUrl.replace(/\/$/, '') + '/api/v1/ingest/shopify-quotes/reserve';
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => null);

    if (res.status === 409 && !(body && body.success)) {
      return {
        conflict: true,
        error:
          (body && body.error) ||
          "Some items don't have enough stock for the quantities requested.",
        details: (body && (body.details || (body.data && body.data.details))) || [],
      };
    }

    if (!res.ok) {
      console.warn('[quote] reserve failed', res.status, body);
      return null;
    }
    if (!body || !body.success || !body.data) return null;
    return {
      id: body.data.id,
      quoteNumber: body.data.quoteNumber,
      uploadToken: body.data.uploadToken || null,
    };
  } catch (err) {
    console.warn('[quote] reserve threw', err);
    return null;
  }
}

async function uploadQuotePdfToCvinsight(baseUrl, apiKey, quoteId, uploadToken, blob) {
  if (!baseUrl || !apiKey || !quoteId) return false;
  try {
    const url =
      baseUrl.replace(/\/$/, '') + '/api/v1/ingest/shopify-quotes/' + encodeURIComponent(quoteId) + '/pdf';
    const headers = {
      'Content-Type': 'application/pdf',
      'x-api-key': apiKey,
    };
    if (uploadToken) headers['X-Quote-Upload-Token'] = uploadToken;
    const res = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: blob,
    });
    if (!res.ok) {
      console.warn('[quote] pdf upload failed', res.status, await res.text().catch(() => ''));
      return false;
    }
    return true;
  } catch (err) {
    console.warn('[quote] pdf upload threw', err);
    return false;
  }
}

function formatCurrency(cents, currencyCode) {
  const amount = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat(navigator.language || 'en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
    }).format(amount);
  } catch (_) {
    return '$' + amount.toFixed(2);
  }
}

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

function buildItemFromProductElement(triggerEl) {
  const jsonId = triggerEl.dataset.productJsonId;
  if (!jsonId) return null;
  const tag = document.getElementById(jsonId);
  if (!tag) return null;
  let product;
  try {
    product = JSON.parse(tag.textContent);
  } catch (_) {
    return null;
  }

  // Find the active product form to read selected variant + qty.
  const productForm = triggerEl.closest('product-form') || document.querySelector('product-form');
  let variantId = null;
  let qty = 1;
  if (productForm) {
    const idInput = productForm.querySelector('[name="id"]');
    if (idInput && idInput.value) variantId = parseInt(idInput.value, 10);
    const qtyInput = productForm.querySelector('[name="quantity"]');
    if (qtyInput && qtyInput.value) qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
  }

  const variant =
    product.variants.find((v) => v.id === variantId) ||
    product.variants.find((v) => v.available) ||
    product.variants[0];

  if (!variant) return null;

  const image = variant.featured_image || (product.featured_image ? { src: product.featured_image } : null);

  // Suppress variant title for single-variant products (Shopify still emits
  // option values like "Default Title" or "1" that add no information).
  const isSingleVariant = !product.variants || product.variants.length <= 1;
  const variantTitle =
    isSingleVariant || variant.title === 'Default Title' ? '' : variant.title;

  return {
    variantId: variant.id,
    title: product.title,
    variantTitle: variantTitle,
    sku: variant.sku || '',
    qty: qty,
    unitPriceCents: variant.price,
    lineTotalCents: variant.price * qty,
    imageSrc: image ? normalizeImageUrl(image.src) : '',
  };
}

// Shopify product JSON emits protocol-relative URLs (`//cdn.shopify.com/...`).
// Anywhere we send the URL to a non-browser consumer (CVInsight ingest),
// it must be fully qualified.
function normalizeImageUrl(src) {
  if (!src) return '';
  if (src.startsWith('//')) return 'https:' + src;
  return src;
}

async function buildItemsFromCart() {
  const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Cart fetch failed');
  const cart = await res.json();
  return cart.items.map((item) => ({
    variantId: item.variant_id,
    title: item.product_title,
    variantTitle: item.product_has_only_default_variant ? '' : item.variant_title || '',
    sku: item.sku || '',
    qty: item.quantity,
    unitPriceCents: item.price,
    lineTotalCents: item.final_line_price,
    imageSrc: normalizeImageUrl(item.image || ''),
  }));
}

class QuoteModal extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.lastPdfBlob = null;
    this.lastPdfFilename = null;
    this.previouslyFocused = null;
    this._buttonState = 'idle';
    // True while any line is known to have zero on hand — keeps the submit
    // button disabled even in the 'idle' button state.
    this._stockBlocked = false;
    this._stockErrorShown = false;
    // Turnstile widget state. The widget renders on first open and refreshes
    // its own token; submit waits on _turnstileWaiters if no token is ready.
    this._turnstileWidgetId = null;
    this._turnstileToken = null;
    this._turnstileWaiters = [];
  }

  connectedCallback() {
    this.form = this.querySelector('#QuoteModalForm');
    this.itemsList = this.querySelector('[data-quote-items-list]');
    this.subtotalEl = this.querySelector('[data-quote-subtotal]');
    this.errorEl = this.querySelector('[data-quote-error]');
    this.successEl = this.querySelector('[data-quote-success]');
    this.warningEl = this.querySelector('[data-quote-warning]');
    this.submitBtn = this.querySelector('[data-quote-submit]');
    this.submitLabel = this.querySelector('.quote-modal__submit-label');
    this.submitSpinner = this.querySelector('.quote-modal__submit-spinner');
    this.submitSuccess = this.querySelector('.quote-modal__submit-success');
    this.dialog = this.querySelector('.quote-modal__dialog');
    this.turnstileEl = this.querySelector('[data-quote-turnstile]');

    document.addEventListener('click', this._onDocClick.bind(this));
    this.querySelectorAll('[data-quote-close]').forEach((el) =>
      el.addEventListener('click', this.close.bind(this))
    );
    this.form.addEventListener('submit', this._onSubmit.bind(this));
    this.itemsList.addEventListener('click', this._onQtyClick.bind(this));
    this.itemsList.addEventListener('change', this._onQtyChange.bind(this));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.hidden) this.close();
    });
    const redownload = this.querySelector('[data-quote-redownload]');
    if (redownload) {
      redownload.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.lastPdfBlob && this.lastPdfFilename) {
          this._triggerDownload(this.lastPdfBlob, this.lastPdfFilename);
        }
      });
    }
  }

  _onDocClick(e) {
    const trigger = e.target.closest('[data-quote-trigger]');
    if (!trigger) return;
    e.preventDefault();
    this._openFromTrigger(trigger).catch((err) => {
      console.error('[quote] failed to open', err);
      // open() clears the error element, so show the message after opening.
      this.open();
      this._showError("Couldn't load your cart. Please refresh and try again.");
    });
  }

  async _openFromTrigger(trigger) {
    const source = trigger.dataset.source;
    let items;
    if (source === 'product') {
      const item = buildItemFromProductElement(trigger);
      items = item ? [item] : [];
    } else {
      items = await buildItemsFromCart();
    }
    if (!items.length) {
      // Clear any lines (and stock block) left over from a previous open.
      this.items = [];
      this._renderItems();
      this.open();
      this._showError('There are no items to quote.');
      return;
    }
    this.items = items;
    this.source = source === 'product' ? 'product' : 'cart';
    // Stock caps come off the trigger synchronously, so the steppers are
    // already capped on the first paint — no flash of an uncapped stepper.
    this._applyInventory(readInventoryFromTrigger(trigger));
    this._renderItems();
    this.open();
  }

  // Stamp each line's on-hand figure from the variant-id -> quantity map.
  // Anything missing or null stays uncapped.
  _applyInventory(map) {
    this.items.forEach((it) => {
      const onHand = map[it.variantId];
      it.onHand = Number.isFinite(onHand) ? Math.max(0, Math.floor(onHand)) : null;
    });
  }

  // Re-clamp every line against its known on-hand figure and refresh the
  // stepper limits, stock notes, subtotal and submit state.
  _applyAvailability() {
    this.itemsList.querySelectorAll('[data-quote-item]').forEach((li) => {
      const item = this.items[parseInt(li.dataset.index, 10)];
      if (!item) return;
      const clamped = this._clampQty(item.qty, item);
      if (clamped !== item.qty) {
        item.qty = clamped;
        item.lineTotalCents = item.unitPriceCents * clamped;
        const input = li.querySelector('[data-quote-qty-input]');
        if (input) input.value = String(clamped);
        this._updateLineMeta(li, item);
      }
      this._updateLineAvailability(li, item);
    });
    this._updateSubtotal();
    this._updateSubmitState();
  }

  _renderItems() {
    const currency = this.dataset.shopCurrency || 'USD';
    this.itemsList.innerHTML = this.items
      .map((it, i) => {
        const variant = it.variantTitle ? `<span class="quote-modal__item-variant"> — ${escapeHtml(it.variantTitle)}</span>` : '';
        const thumb = it.imageSrc
          ? `<img class="quote-modal__item-thumb" src="${escapeHtml(shopifyImageUrl(it.imageSrc, 120))}" alt="" loading="lazy" />`
          : `<span class="quote-modal__item-thumb quote-modal__item-thumb--empty" aria-hidden="true"></span>`;
        const unit = formatCurrency(it.unitPriceCents, currency);
        const line = formatCurrency(it.lineTotalCents, currency);
        const titleText = escapeHtml(it.title);
        return `<li class="quote-modal__item" data-quote-item data-index="${i}">
          ${thumb}
          <span class="quote-modal__item-text">
            <span class="quote-modal__item-title">${titleText}${variant}</span>
            <span class="quote-modal__item-meta" data-quote-item-meta>${unit} × ${it.qty} = ${line}</span>
          </span>
          <div class="quote-modal__item-qty-wrap">
            <div class="quote-modal__item-qty">
              <button type="button" class="quote-modal__qty-btn" data-quote-qty-step="-1" aria-label="Decrease quantity for ${titleText}">−</button>
              <input type="number" class="quote-modal__qty-input" min="1" step="1" value="${it.qty}" data-quote-qty-input aria-label="Quantity for ${titleText}" />
              <button type="button" class="quote-modal__qty-btn" data-quote-qty-step="1" aria-label="Increase quantity for ${titleText}">+</button>
            </div>
            <span class="quote-modal__item-stock" data-quote-item-stock aria-live="polite" hidden></span>
          </div>
        </li>`;
      })
      .join('');
    this._applyAvailability();
  }

  // Clamps to [1, onHand]. A line with onHand 0 still displays 1 — it's
  // blocked outright rather than shown as a zero-qty line.
  _clampQty(n, item) {
    const parsed = parseInt(n, 10);
    let next = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    if (item && Number.isFinite(item.onHand)) {
      next = Math.min(next, Math.max(1, item.onHand));
    }
    return next;
  }

  // Applies this line's on-hand cap to the stepper and renders the stock note.
  _updateLineAvailability(li, item) {
    const tracked = Number.isFinite(item.onHand);
    const blocked = tracked && item.onHand === 0;
    const input = li.querySelector('[data-quote-qty-input]');
    const plus = li.querySelector('[data-quote-qty-step="1"]');
    const minus = li.querySelector('[data-quote-qty-step="-1"]');
    const note = li.querySelector('[data-quote-item-stock]');

    li.classList.toggle('quote-modal__item--blocked', blocked);

    if (input) {
      if (tracked) input.max = String(Math.max(1, item.onHand));
      else input.removeAttribute('max');
      input.disabled = blocked;
    }
    if (plus) plus.disabled = blocked || (tracked && item.qty >= item.onHand);
    if (minus) minus.disabled = blocked;

    if (!note) return;
    if (blocked) {
      note.textContent = 'Out of stock';
    } else if (tracked) {
      note.textContent = `${item.onHand} Available`;
    } else {
      note.textContent = '';
    }
    note.hidden = !note.textContent;
  }

  // A line with zero on hand blocks the whole quote until it's removed.
  _updateSubmitState() {
    this._stockBlocked = this.items.some((it) => it.onHand === 0);
    if (this._stockBlocked) {
      this._showError(OUT_OF_STOCK_MESSAGE);
      this._stockErrorShown = true;
    } else if (this._stockErrorShown) {
      this._stockErrorShown = false;
      this.errorEl.hidden = true;
    }
    this._setButtonState(this._buttonState);
  }

  // Re-clamp from the `details` array a 409 /reserve response carries, so the
  // steppers reflect what CVInsight just told us is actually available.
  _applyConflictDetails(details) {
    if (!Array.isArray(details)) return;
    const available = new Map();
    details.forEach((d) => {
      if (!d || !d.sku) return;
      const n = Number(d.available);
      if (!Number.isFinite(n)) return;
      available.set(String(d.sku).toUpperCase(), Math.max(0, Math.floor(n)));
    });
    if (!available.size) return;
    this.items.forEach((it) => {
      if (!it.sku) return;
      const n = available.get(it.sku.toUpperCase());
      if (Number.isFinite(n)) it.onHand = n;
    });
    this._applyAvailability();
  }

  _onQtyClick(e) {
    const btn = e.target.closest('[data-quote-qty-step]');
    if (!btn) return;
    const li = btn.closest('[data-quote-item]');
    if (!li) return;
    const index = parseInt(li.dataset.index, 10);
    const item = this.items[index];
    if (!item) return;
    const step = parseInt(btn.dataset.quoteQtyStep, 10) || 0;
    const next = this._clampQty(item.qty + step, item);
    if (next === item.qty) return;
    item.qty = next;
    item.lineTotalCents = item.unitPriceCents * next;
    const input = li.querySelector('[data-quote-qty-input]');
    if (input) input.value = String(next);
    this._updateLineMeta(li, item);
    this._updateLineAvailability(li, item);
    this._updateSubtotal();
    this._resetAfterEdit();
  }

  _onQtyChange(e) {
    const input = e.target.closest('[data-quote-qty-input]');
    if (!input) return;
    const li = input.closest('[data-quote-item]');
    if (!li) return;
    const index = parseInt(li.dataset.index, 10);
    const item = this.items[index];
    if (!item) return;
    const next = this._clampQty(input.value, item);
    const changed = next !== item.qty;
    item.qty = next;
    item.lineTotalCents = item.unitPriceCents * next;
    input.value = String(next);
    this._updateLineMeta(li, item);
    this._updateLineAvailability(li, item);
    this._updateSubtotal();
    if (changed) this._resetAfterEdit();
  }

  _updateLineMeta(li, item) {
    const meta = li.querySelector('[data-quote-item-meta]');
    if (!meta) return;
    const currency = this.dataset.shopCurrency || 'USD';
    const unit = formatCurrency(item.unitPriceCents, currency);
    const line = formatCurrency(item.lineTotalCents, currency);
    meta.textContent = `${unit} × ${item.qty} = ${line}`;
  }

  _updateSubtotal() {
    if (!this.subtotalEl) return;
    if (!this.items.length) {
      this.subtotalEl.hidden = true;
      return;
    }
    const currency = this.dataset.shopCurrency || 'USD';
    const cents = this.items.reduce((s, it) => s + it.lineTotalCents, 0);
    this.subtotalEl.textContent = `Subtotal: ${formatCurrency(cents, currency)}`;
    this.subtotalEl.hidden = false;
  }

  // After the customer submits and the button enters its success state,
  // editing a quantity should re-enable the button so they can regenerate
  // a fresh PDF that reflects the new qtys.
  _resetAfterEdit() {
    if (!this.submitBtn || !this.submitBtn.classList.contains('is-success')) return;
    this._setButtonState('idle');
    this.successEl.hidden = true;
    if (this.warningEl) this.warningEl.hidden = true;
  }

  // Write the current modal quantities back to the Shopify cart. Throws on
  // failure so _onSubmit can surface a non-blocking warning. Product-page
  // flow: replace qty for that variant (or add if not in cart). Cart-drawer
  // flow: one /cart/update.js call sets all variants to their modal qty.
  async _syncCart() {
    const items = this.items.filter((it) => Number.isFinite(it.variantId));
    if (!items.length) return;

    const jsonHeaders = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (this.source === 'cart') {
      const updates = {};
      for (const it of items) updates[it.variantId] = it.qty;
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ updates: updates }),
      });
      if (!res.ok) throw new Error('cart update failed ' + res.status);
      return;
    }

    // Product page: single item. Try /cart/change.js (sets absolute qty if
    // the variant is already in the cart); on 422 fall back to /cart/add.js.
    const it = items[0];
    const changeRes = await fetch('/cart/change.js', {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ id: it.variantId, quantity: it.qty }),
    });
    if (changeRes.ok) return;
    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ items: [{ id: it.variantId, quantity: it.qty }] }),
    });
    if (!addRes.ok) throw new Error('cart add failed ' + addRes.status);
  }

  open() {
    this.previouslyFocused = document.activeElement;
    this.hidden = false;
    document.body.classList.add('quote-modal-open');
    this.successEl.hidden = true;
    if (this.warningEl) this.warningEl.hidden = true;
    this.form.hidden = false;
    this.errorEl.hidden = true;
    this._stockErrorShown = false;
    this._setButtonState('idle');
    // Re-assert any stock block cleared by hiding the error above.
    this._updateSubmitState();
    // Start the bot check now so a token is usually ready by submit.
    this._ensureTurnstile();
    // Focus first field next tick, but don't steal focus from a field
    // the user already clicked into (breaks password-manager dropdowns).
    requestAnimationFrame(() => {
      if (this.contains(document.activeElement)) return;
      const firstField = this.form.querySelector('input,textarea,button');
      if (firstField) firstField.focus();
    });
  }

  close() {
    this.hidden = true;
    document.body.classList.remove('quote-modal-open');
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus();
    }
  }

  // Render the Turnstile widget once. Only runs when both a site key and
  // CVInsight ingest are configured — the token is only sent to /reserve.
  // A load failure (blocked script, offline) is non-fatal: reserve goes out
  // without a token and CVInsight decides.
  async _ensureTurnstile() {
    const siteKey = this.dataset.turnstileSiteKey || '';
    if (!siteKey || !this.dataset.ingestBaseUrl || !this.turnstileEl) return;
    if (this._turnstileWidgetId !== null) return;
    try {
      await ensureTurnstileLoaded();
      // A second open while the script loaded may have rendered already.
      if (this._turnstileWidgetId !== null) return;
      this._turnstileWidgetId = window.turnstile.render(this.turnstileEl, {
        sitekey: siteKey,
        action: 'quote_reserve',
        appearance: 'interaction-only',
        callback: (token) => {
          this._turnstileToken = token;
          this._turnstileWaiters.splice(0).forEach((resolve) => resolve(token));
        },
        'expired-callback': () => {
          this._turnstileToken = null;
        },
        'error-callback': () => {
          this._turnstileToken = null;
          // Returning a value tells Turnstile we handled it (no console throw);
          // it retries on its own.
          return true;
        },
      });
    } catch (err) {
      console.warn('[quote] turnstile unavailable', err);
    }
  }

  // Resolve with the current Turnstile token, waiting up to TURNSTILE_WAIT_MS
  // for one (e.g. while the shopper completes a challenge). Resolves null when
  // Turnstile isn't configured or never produced a token.
  async _getTurnstileToken() {
    await this._ensureTurnstile();
    if (this._turnstileWidgetId === null) return null;
    if (this._turnstileToken) return this._turnstileToken;
    return new Promise((resolve) => {
      this._turnstileWaiters.push(resolve);
      setTimeout(() => {
        const i = this._turnstileWaiters.indexOf(resolve);
        if (i !== -1) this._turnstileWaiters.splice(i, 1);
        resolve(null);
      }, TURNSTILE_WAIT_MS);
    });
  }

  // Tokens are single-use: after a reserve attempt, discard it and have the
  // widget fetch a fresh one for any resubmit.
  _resetTurnstile() {
    this._turnstileToken = null;
    if (this._turnstileWidgetId !== null && window.turnstile) {
      window.turnstile.reset(this._turnstileWidgetId);
    }
  }

  _showError(msg) {
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  _setButtonState(state) {
    // state: 'idle' | 'submitting' | 'success'
    this._buttonState = state;
    this.submitBtn.disabled = state !== 'idle' || this._stockBlocked;
    this.submitBtn.classList.toggle('is-success', state === 'success');
    this.submitLabel.hidden = state !== 'idle';
    this.submitSpinner.hidden = state !== 'submitting';
    this.submitSuccess.hidden = state !== 'success';
  }

  async _onSubmit(e) {
    e.preventDefault();
    this.errorEl.hidden = true;

    const data = new FormData(this.form);
    const name = (data.get('contact[name]') || '').toString().trim();
    const email = (data.get('contact[email]') || '').toString().trim();
    if (!name || !email) {
      this._showError('Name and email are required.');
      return;
    }

    if (this._stockBlocked) {
      this._showError(OUT_OF_STOCK_MESSAGE);
      return;
    }

    this._setButtonState('submitting');

    const validForDays = parseInt(this.dataset.validForDays, 10) || 30;
    const issueDate = new Date();
    const validUntil = new Date(issueDate.getTime() + validForDays * 86400000);
    const currency = this.dataset.shopCurrency || 'USD';

    const formInfo = {
      name: name,
      company: (data.get('contact[company_name]') || '').toString(),
      email: email,
      phone: (data.get('contact[phone]') || '').toString(),
      project: (data.get('contact[project-name]') || '').toString(),
      deliveryDate: (data.get('contact[delivery-date]') || '').toString(),
      instructions: (data.get('instructions') || '').toString(),
    };

    // 1. Reserve a canonical quote number from CVInsight (best-effort).
    const ingestBaseUrl = this.dataset.ingestBaseUrl || '';
    const ingestApiKey = this.dataset.ingestApiKey || '';
    const reservePayload = {
      source: 'shopify',
      currency: currency,
      contact: {
        name: formInfo.name,
        company: formInfo.company || null,
        email: formInfo.email,
        phone: formInfo.phone || null,
        project: formInfo.project || null,
        deliveryDate: formInfo.deliveryDate || null,
        instructions: formInfo.instructions || null,
      },
      items: this.items.map((it) => ({
        title: it.title,
        variantTitle: it.variantTitle || null,
        sku: it.sku || null,
        qty: it.qty,
        unitPriceCents: it.unitPriceCents,
        lineTotalCents: it.lineTotalCents,
        imageUrl: it.imageSrc || null,
      })),
    };

    let reserved = null;
    if (ingestBaseUrl && ingestApiKey) {
      reservePayload.turnstileToken = await this._getTurnstileToken();
      reserved = await reserveQuoteFromCvinsight(ingestBaseUrl, ingestApiKey, reservePayload);
      this._resetTurnstile();
    }

    // 1a. Stock moved under us. Re-clamp the steppers to what CVInsight says
    //     is actually available and let the customer resubmit. Nothing is
    //     emailed, written to the cart, or downloaded.
    if (reserved && reserved.conflict) {
      this._applyConflictDetails(reserved.details);
      this._setButtonState('idle');
      this._showError(reserved.error);
      this._stockErrorShown = true;
      return;
    }

    const quoteNumber = (reserved && reserved.quoteNumber) || generateLocalQuoteNumber();
    const ingestQuoteId = reserved ? reserved.id : null;
    const ingestUploadToken = reserved ? reserved.uploadToken : null;

    // 1b. Sync the modal's quantities back to the Shopify cart (best-effort).
    //     Runs only after the reserve succeeds, so a rejected quote never
    //     leaves an over-quantity cart behind. Failure surfaces as a
    //     non-blocking warning on the success screen; the PDF and email path
    //     continue regardless.
    let cartSyncFailed = false;
    try {
      await this._syncCart();
    } catch (err) {
      console.warn('[quote] cart sync failed', err);
      cartSyncFailed = true;
    }

    // 2. POST to Shopify /contact form (existing email path).
    try {
      await this._postToContact(quoteNumber, formInfo);
    } catch (err) {
      console.warn('[quote] contact form post failed', err);
    }

    // 3. Build the PDF.
    let pdfBlob;
    let pdfFilename;
    try {
      await ensureLibsLoaded();
      const out = await this._buildPdf(quoteNumber, issueDate, validUntil, formInfo);
      pdfBlob = out.blob;
      pdfFilename = out.filename;
    } catch (err) {
      console.error('[quote] pdf generation failed', err);
      this._setButtonState('idle');
      this._showError('Something went wrong generating your quote. Please try again.');
      return;
    }

    this.lastPdfBlob = pdfBlob;
    this.lastPdfFilename = pdfFilename;

    // 4. Upload the PDF to CVInsight (fire-and-forget; doesn't block download).
    if (ingestQuoteId) {
      uploadQuotePdfToCvinsight(
        ingestBaseUrl,
        ingestApiKey,
        ingestQuoteId,
        ingestUploadToken,
        pdfBlob
      ).catch(() => {});
    }

    // 5. Download for the customer.
    this._triggerDownload(pdfBlob, pdfFilename);

    this._setButtonState('success');
    this.successEl.hidden = false;
    if (cartSyncFailed && this.warningEl) {
      this.warningEl.textContent =
        "We couldn't update your cart to match this quote. Your PDF is ready.";
      this.warningEl.hidden = false;
    }
  }

  _postToContact(quoteNumber, info) {
    const currency = this.dataset.shopCurrency || 'USD';
    const lines = this.items
      .map((it, i) => {
        const variant = it.variantTitle ? ` (${it.variantTitle})` : '';
        const sku = it.sku ? ` SKU ${it.sku} —` : '';
        return `${i + 1}. ${it.title}${variant} —${sku} Qty ${it.qty} — ${formatCurrency(
          it.unitPriceCents,
          currency
        )} ea — ${formatCurrency(it.lineTotalCents, currency)}`;
      })
      .join('\n');
    const subtotalCents = this.items.reduce((s, it) => s + it.lineTotalCents, 0);
    const body =
      (info.instructions ? info.instructions + '\n\n' : '') +
      `--- Items ---\n${lines}\nSubtotal: ${formatCurrency(subtotalCents, currency)}`;

    // Build FormData from the rendered {% form 'contact' %} so Shopify's
    // injected hidden fields (form_type, utf8, bot-protection tokens) are
    // included. Then add the dynamic computed fields on top.
    const fd = new FormData(this.form);
    fd.delete('instructions');
    fd.set('contact[quote_number]', quoteNumber);
    fd.set('contact[body]', body);

    const action = this.form.getAttribute('action') || '/contact';
    return fetch(action, {
      method: 'POST',
      body: fd,
      redirect: 'follow',
      credentials: 'same-origin',
      headers: { Accept: 'text/html,application/xhtml+xml' },
    }).then(async (r) => {
      const responseText = await r.text();
      if (!r.ok) {
        console.error(
          '[quote] /contact returned',
          r.status,
          r.statusText,
          'final URL:',
          r.url
        );
        throw new Error('Contact post non-ok: ' + r.status);
      }

      if (!this._contactPostSucceeded(r.url, responseText)) {
        console.error('[quote] /contact did not confirm success', 'final URL:', r.url);
        throw new Error('Contact post missing success confirmation');
      }

      return true;
    });
  }

  _contactPostSucceeded(finalUrl, responseText) {
    if (finalUrl && /[?&]contact_posted=true\b/.test(finalUrl)) {
      return true;
    }

    if (!responseText) {
      return false;
    }

    try {
      const doc = new DOMParser().parseFromString(responseText, 'text/html');
      return Boolean(doc.querySelector('[data-form-posted-success="true"]'));
    } catch (err) {
      console.warn('[quote] failed to parse contact response', err);
      return false;
    }
  }

  async _buildPdf(quoteNumber, issueDate, validUntil, info) {
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    const currency = this.dataset.shopCurrency || 'USD';

    // --- Header ---
    let y = margin;

    if (this.dataset.logoUrl) {
      const logoData = await fetchAsDataURL(this.dataset.logoUrl);
      if (logoData) {
        try {
          const natural = await getImageNaturalSize(logoData);
          const maxW = 160;
          const maxH = 70;
          let lw = maxW;
          let lh = maxH;
          if (natural.w && natural.h) {
            const ratio = natural.w / natural.h;
            if (ratio >= maxW / maxH) {
              lw = maxW;
              lh = maxW / ratio;
            } else {
              lh = maxH;
              lw = maxH * ratio;
            }
          }
          const fmt = /^data:image\/(png|jpeg|jpg|webp)/i.exec(logoData);
          const fmtStr = fmt ? fmt[1].toUpperCase().replace('JPG', 'JPEG') : 'PNG';
          doc.addImage(logoData, fmtStr, margin, y, lw, lh, undefined, 'FAST');
        } catch (err) {
          console.warn('[quote] failed to render logo', err);
        }
      }
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('QUOTE', pageWidth - margin, y + 18, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(quoteNumber, pageWidth - margin, y + 36, { align: 'right' });
    doc.text(`Date: ${formatDate(issueDate)}`, pageWidth - margin, y + 50, { align: 'right' });
    doc.text(`Valid until: ${formatDate(validUntil)}`, pageWidth - margin, y + 64, { align: 'right' });

    y += 90;

    // --- Sender / Recipient ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(this.dataset.shopName || 'Too Many Amps', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    let senderY = y + 14;
    const addr = (this.dataset.businessAddress || '').split(/\r?\n/).filter(Boolean);
    addr.forEach((line) => {
      doc.text(line, margin, senderY);
      senderY += 12;
    });
    if (this.dataset.businessPhone) {
      doc.text(this.dataset.businessPhone, margin, senderY);
      senderY += 12;
    }
    if (this.dataset.businessEmail) {
      doc.text(this.dataset.businessEmail, margin, senderY);
      senderY += 12;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Quote For:', pageWidth / 2 + 20, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    let recipY = y + 14;
    const recipientLines = [
      info.name,
      info.company,
      info.email,
      info.phone,
      info.project ? `Project: ${info.project}` : '',
      info.deliveryDate ? `Needed: ${info.deliveryDate}` : '',
    ].filter(Boolean);
    recipientLines.forEach((line) => {
      doc.text(line, pageWidth / 2 + 20, recipY);
      recipY += 12;
    });

    y = Math.max(senderY, recipY) + 10;

    // --- Line items table ---
    const thumbCache = {};
    await Promise.all(
      this.items.map(async (it) => {
        if (!it.imageSrc) return;
        const url = shopifyImageUrl(it.imageSrc, 200);
        thumbCache[it.imageSrc] = await fetchAsDataURL(url);
      })
    );

    const subtotalCents = this.items.reduce((s, it) => s + it.lineTotalCents, 0);

    const body = this.items.map((it) => {
      const desc = it.variantTitle ? `${it.title}\n${it.variantTitle}` : it.title;
      return [
        '', // image cell drawn in didDrawCell
        desc,
        it.sku,
        String(it.qty),
        formatCurrency(it.unitPriceCents, currency),
        formatCurrency(it.lineTotalCents, currency),
      ];
    });

    doc.autoTable({
      startY: y,
      margin: { left: margin, right: margin },
      head: [[
        '',
        'Description',
        'SKU',
        { content: 'Qty', styles: { halign: 'center' } },
        { content: 'Price', styles: { halign: 'right' } },
        { content: 'Total', styles: { halign: 'right' } },
      ]],
      body: body,
      styles: { fontSize: 9, cellPadding: 6, valign: 'middle' },
      headStyles: { fillColor: [17, 17, 17], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 50, minCellHeight: 50 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 'wrap', overflow: 'visible' },
        3: { cellWidth: 35, halign: 'center' },
        4: { cellWidth: 65, halign: 'right' },
        5: { cellWidth: 65, halign: 'right' },
      },
      didDrawCell: (data) => {
        if (data.section !== 'body' || data.column.index !== 0) return;
        const item = this.items[data.row.index];
        if (!item || !item.imageSrc) return;
        const dataUrl = thumbCache[item.imageSrc];
        if (!dataUrl) return;
        const size = Math.min(data.cell.width - 4, data.cell.height - 4, 44);
        const cx = data.cell.x + (data.cell.width - size) / 2;
        const cy = data.cell.y + (data.cell.height - size) / 2;
        try {
          doc.addImage(dataUrl, 'JPEG', cx, cy, size, size, undefined, 'FAST');
        } catch (_) {
          // ignore
        }
      },
    });

    let endY = doc.lastAutoTable.finalY + 14;

    // --- Subtotal ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`Subtotal: ${formatCurrency(subtotalCents, currency)}`, pageWidth - margin, endY, {
      align: 'right',
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('(Tax and shipping not included)', pageWidth - margin, endY + 12, { align: 'right' });
    endY += 32;

    // --- Special instructions ---
    if (info.instructions) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Special Instructions:', margin, endY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const wrapped = doc.splitTextToSize(info.instructions, pageWidth - margin * 2);
      doc.text(wrapped, margin, endY + 14);
      endY += 14 + wrapped.length * 11 + 10;
    }

    // --- Footer text + phone (anchored to bottom of last page, centered) ---
    const footerText = this.dataset.footerText;
    const footerPhone = this.dataset.businessPhone;
    if (footerText || footerPhone) {
      const pageCount = doc.getNumberOfPages();
      doc.setPage(pageCount);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const pageHeight = doc.internal.pageSize.getHeight();
      const lineHeight = 10;
      const wrapped = footerText ? doc.splitTextToSize(footerText, pageWidth - margin * 2) : [];
      const totalLines = wrapped.length + (footerPhone ? 1 : 0);
      const totalHeight = totalLines * lineHeight;
      let lineY = pageHeight - margin - totalHeight + lineHeight;
      if (wrapped.length) {
        doc.text(wrapped, pageWidth / 2, lineY, { align: 'center' });
        lineY += wrapped.length * lineHeight;
      }
      if (footerPhone) {
        doc.text(footerPhone, pageWidth / 2, lineY, { align: 'center' });
      }
    }

    const filename = `TMA-Quote-${quoteNumber}.pdf`;
    const blob = doc.output('blob');
    return { blob, filename };
  }

  _triggerDownload(blob, filename) {
    // Chrome falls back to naming the file after the blob URL's UUID (no
    // extension) when the URL is revoked before its download manager has
    // resolved the filename, which can take seconds while the file is scanned.
    // So keep one URL per PDF alive and revoke it only when a new PDF
    // replaces it; the "click here" re-download reuses it.
    if (this._pdfUrlBlob !== blob) {
      if (this._pdfUrl) URL.revokeObjectURL(this._pdfUrl);
      this._pdfUrl = URL.createObjectURL(
        new File([blob], filename, { type: 'application/pdf' })
      );
      this._pdfUrlBlob = blob;
    }
    const a = document.createElement('a');
    a.href = this._pdfUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

function escapeHtml(str) {
  return String(str || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c]));
}

if (!customElements.get('quote-modal')) {
  customElements.define('quote-modal', QuoteModal);
}
